<script setup>
import { ref, watch, onBeforeUnmount, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import { toast } from '@/services/toast'
import { createSubmitFeedbackResource } from '@/services/feedback'
import { getErrorMessage } from '@/utils/error'

const { t } = useI18n()
const visible = ref(false)
const feedbackType = ref('Suggestion')
const message = ref('')
const error = ref('')
const submitResource = createSubmitFeedbackResource()
const feedbackTypes = [
  { value: 'Bug', label: () => t('feedback.bug') },
  { value: 'Suggestion', label: () => t('feedback.suggestion') },
  { value: 'Other', label: () => t('feedback.other') },
]

const typeOptions = feedbackTypes.map((option) => ({
  value: option.value,
  get label() {
    return option.label()
  },
}))

function open() {
  feedbackType.value = 'Suggestion'
  message.value = ''
  error.value = ''
  visible.value = true
}

function close() {
  if (!submitResource.loading) visible.value = false
}

async function submit() {
  error.value = ''
  if (!message.value.trim()) {
    error.value = t('feedback.messageRequired')
    return
  }
  if (submitResource.loading) return

  try {
    await submitResource.fetch({
      feedback_type: feedbackType.value,
      message: message.value.trim(),
      page_url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    })
    toast(t('feedback.submitSuccess'), 'success')
    visible.value = false
    message.value = ''
  } catch (requestError) {
    error.value = getErrorMessage(requestError, t('feedback.submitError'))
    toast(error.value, 'error')
  }
}

onMounted(() => window.addEventListener('retiora:open-feedback', open))
onBeforeUnmount(() => window.removeEventListener('retiora:open-feedback', open))

watch(visible, (isVisible) => {
  if (!isVisible) error.value = ''
})
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="$t('feedback.title')"
    :style="{ width: 'min(480px, calc(100vw - 32px))' }"
    :breakpoints="{ '640px': 'calc(100vw - 32px)' }"
    :closable="!submitResource.loading"
    @hide="close"
  >
    <p class="feedback-intro">{{ $t('feedback.description') }}</p>

    <div class="feedback-field">
      <label for="feedback-type">{{ $t('feedback.type') }}</label>
      <Select
        id="feedback-type"
        v-model="feedbackType"
        :options="typeOptions"
        option-label="label"
        option-value="value"
        fluid
        :disabled="submitResource.loading"
      />
    </div>

    <div class="feedback-field">
      <label for="feedback-message">{{ $t('feedback.message') }}</label>
      <Textarea
        id="feedback-message"
        v-model="message"
        rows="5"
        auto-resize
        fluid
        :placeholder="$t('feedback.messagePlaceholder')"
        :disabled="submitResource.loading"
        @keydown.meta.enter="submit"
        @keydown.ctrl.enter="submit"
      />
    </div>

    <p
      v-if="error"
      class="feedback-error"
      role="alert"
    >{{ error }}</p>

    <div class="feedback-actions">
      <Button
        type="button"
        severity="secondary"
        outlined
        :disabled="submitResource.loading"
        @click="close"
      >{{ $t('common.cancel') }}</Button>
      <Button
        type="button"
        :loading="submitResource.loading"
        :label="$t('feedback.send')"
        @click="submit"
      />
    </div>
  </Dialog>
</template>

<style scoped>
.feedback-intro {
  margin: 0 0 18px;
  color: #5f6070;
  line-height: 1.5;
}
.feedback-field {
  display: grid;
  gap: 7px;
  margin-top: 16px;
}
.feedback-field label {
  color: #303142;
  font-size: 14px;
  font-weight: 600;
}
.feedback-error {
  margin: 14px 0 0;
  color: #b42318;
  font-size: 14px;
}
.feedback-actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  margin-top: 24px;
}
</style>
